import { Box } from '@mui/material';
import { SxProps } from '@mui/system';
import {
  ChangeEvent,
  ClipboardEvent,
  ForwardedRef,
  forwardRef,
  KeyboardEvent,
  useRef,
} from 'react';
import { ControllerRenderProps } from 'react-hook-form';
import { FormErrorText, InputField } from './otpInput.styles';

const digitRegex = new RegExp('^[0-9]$');
const pasteRegex = new RegExp(`^[0-9]{0,${length}}$`);

interface IOtpInput
  extends Omit<ControllerRenderProps<{ code: string }, 'code'>, 'ref'> {
  autoFocus?: boolean;
  length?: number;
  sx?: SxProps;
  errorMessage?: string;
}

// Adapted from: https://mui.com/base-ui/react-input/#otp-input
export const OtpInput = forwardRef(
  (
    {
      autoFocus,
      disabled,
      errorMessage,
      length = 6,
      name,
      onBlur,
      onChange,
      sx,
      value,
    }: IOtpInput,
    ref: ForwardedRef<HTMLDivElement>,
  ) => {
    const inputRefs = useRef<(HTMLInputElement | null)[]>(
      new Array<HTMLInputElement | null>(length).fill(null),
    );

    const focusInput = (targetIndex: number) => {
      const targetInput = inputRefs.current[targetIndex];
      targetInput?.focus();
      targetInput?.select();
    };

    const handleKeyDown = (
      event: KeyboardEvent<HTMLInputElement>,
      currentIndex: number,
    ) => {
      switch (event.key) {
        case 'ArrowUp':
        case 'ArrowDown':
        case ' ':
          event.preventDefault();
          break;
        case 'ArrowLeft':
          event.preventDefault();
          if (currentIndex > 0) {
            focusInput(currentIndex - 1);
          }
          break;
        case 'ArrowRight':
          event.preventDefault();
          if (currentIndex < length - 1) {
            focusInput(currentIndex + 1);
          }
          break;
        case 'Delete':
        case 'Backspace':
          event.preventDefault();
          // Using === undefined because `''` can result in unexpected behaviors
          if (value[currentIndex] === undefined && currentIndex > 0) {
            focusInput(currentIndex - 1);
          }

          onChange(
            value.slice(0, currentIndex) + value.slice(currentIndex + 1),
          );

          break;

        default:
          break;
      }
    };

    const handleChange = (
      event: ChangeEvent<HTMLInputElement>,
      currentIndex: number,
    ) => {
      const currentValue = event.target.value;

      if (!currentValue.match(digitRegex)) {
        // This makes sure the input value remains selected,
        // so that the next user input will replace the current value.
        // This is noticeable when the user types an invalid value, such as "a",
        // on an input that already has a value. Without this, the next user input
        // would be appended to the original, which would fail the regex check every time.
        event.target.value = value[currentIndex];
        focusInput(currentIndex);
        return;
      }

      const maximumAllowedIndex = value.length;
      const indexToEnter =
        currentIndex > maximumAllowedIndex ? maximumAllowedIndex : currentIndex;

      const otpArray = value.split('');
      const lastValue = currentValue[currentValue.length - 1];
      otpArray[indexToEnter] = lastValue;
      const newValue = otpArray.join('');

      onChange(newValue);

      const nextIndex =
        indexToEnter < length - 1 ? indexToEnter + 1 : indexToEnter;
      focusInput(nextIndex);
    };

    const handleClick = (currentIndex: number) => {
      focusInput(currentIndex);
    };

    const handlePaste = (
      event: ClipboardEvent<HTMLInputElement>,
      currentIndex: number,
    ) => {
      event.preventDefault();
      const clipboardData = event.clipboardData;

      // Check if there is text data in the clipboard
      if (clipboardData.types.includes('text/plain')) {
        let pastedText = clipboardData.getData('text/plain');
        pastedText = pastedText.substring(0, length).trim();

        if (!pastedText.match(pasteRegex)) {
          return;
        }

        const maximumAllowedIndex = value.length;
        const indexToEnter =
          currentIndex > maximumAllowedIndex
            ? maximumAllowedIndex
            : currentIndex;

        const otpArray = value.split('');

        for (let i = indexToEnter; i < length; i += 1) {
          const lastValue = pastedText[i - indexToEnter] ?? ' ';
          otpArray[i] = lastValue;
        }

        onChange(otpArray.join(''));
      }
    };

    return (
      <Box ref={ref} sx={{ width: '100%', ...sx }} aria-label="OTP input">
        <Box
          sx={{
            display: 'flex',
            gap: 4,
            justifyContent: 'space-between',
          }}
        >
          {new Array(length).fill(null).map((_, index) => (
            <InputField
              key={index}
              inputRef={(element: HTMLInputElement) => {
                inputRefs.current[index] = element;
              }}
              name={`${name}-${index}`}
              disabled={disabled}
              onKeyDown={(event: KeyboardEvent<HTMLInputElement>) =>
                handleKeyDown(event, index)
              }
              onInput={(event: ChangeEvent<HTMLInputElement>) =>
                handleChange(event, index)
              }
              onPaste={(event: ClipboardEvent<HTMLInputElement>) =>
                handlePaste(event, index)
              }
              onClick={() => handleClick(index)}
              onBlur={onBlur}
              value={value[index] ?? ''}
              slotProps={{
                input: {
                  autoFocus: autoFocus && index === 0,
                  'aria-label': `Digit ${index + 1} of OTP`,
                },
              }}
              hasError={Boolean(errorMessage)}
            />
          ))}
        </Box>
        <FormErrorText>{errorMessage}</FormErrorText>
      </Box>
    );
  },
);
